import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { PublicNav, PublicFooter } from "@/components/PublicShell";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Rocket, Boxes, Network, ClipboardCheck, Briefcase, Users, GitBranch, Presentation,
  Check, ArrowRight, ArrowDown, Compass, Search, Target, Wrench, LifeBuoy, Loader2, CheckCircle2,
} from "lucide-react";

const CONTACT = "#advisory-contact";

const STAGES = ["Exploring", "Launching", "Growing", "Scaling"];
const SERVICE_OPTIONS = ["Executive Search", "Accelerator Design", "Incubator Strategy", "Fractional Leadership", "Ecosystem Development", "Program Audit"];

const SERVICES = [
  {
    icon: Rocket,
    title: "Accelerator Design",
    body: "Launch accelerator programs from concept to execution.",
    kind: "Includes",
    items: ["Curriculum development", "Program structure", "Mentor strategy", "Demo Day planning", "KPI framework", "Founder experience"],
  },
  {
    icon: Boxes,
    title: "Incubator Development",
    body: "Design startup support programs that create measurable impact.",
    kind: "Includes",
    items: ["Membership models", "Founder onboarding", "Programming calendar", "Community engagement", "Resource library"],
  },
  {
    icon: Network,
    title: "Ecosystem Strategy",
    body: "Strengthen regional innovation ecosystems.",
    kind: "Includes",
    items: ["Stakeholder mapping", "Partnership strategy", "Founder pipeline", "Investor engagement", "Community building"],
  },
  {
    icon: ClipboardCheck,
    title: "Innovation Program Audits",
    body: "Evaluate existing programs.",
    kind: "Deliverables",
    items: ["Gap analysis", "Recommendations", "Benchmarking", "Growth roadmap"],
  },
  {
    icon: Briefcase,
    title: "Fractional Leadership",
    body: "Experienced leadership without a full-time hire.",
    kind: "Examples",
    items: ["Fractional Managing Director", "Fractional Head of Programs", "Fractional Community Director", "Fractional Innovation Lead"],
  },
  {
    icon: Users,
    title: "Mentor Network Development",
    body: "Build high-value mentor communities.",
    kind: "Includes",
    items: ["Mentor recruitment", "Mentor onboarding", "Matching framework", "Engagement strategy"],
  },
  {
    icon: GitBranch,
    title: "Startup Pipeline Strategy",
    body: "Help organizations attract stronger founders.",
    kind: "Includes",
    items: ["Outreach strategy", "Marketing campaigns", "University partnerships", "Founder sourcing", "Application review process"],
  },
  {
    icon: Presentation,
    title: "Demo Day Consulting",
    body: "Create investor-ready showcase events.",
    kind: "Includes",
    items: ["Event strategy", "Investor outreach", "Founder coaching", "Agenda design", "Logistics"],
  },
];

const PROCESS = [
  { icon: Compass, title: "Discovery", body: "Understand your goals." },
  { icon: Search, title: "Assessment", body: "Review current programs." },
  { icon: Target, title: "Strategy", body: "Develop recommendations." },
  { icon: Wrench, title: "Implementation", body: "Help execute." },
  { icon: LifeBuoy, title: "Ongoing Support", body: "Continuous advisory and recruiting." },
];

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function AdvisoryServices() {
  const [form, setForm] = useState({ full_name: "", email: "", organization: "", role: "", message: "" });
  const [stages, setStages] = useState([]);
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/advisory-inquiry", { ...form, program_stages: stages, services });
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#09090B]" data-testid="advisory-page">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-16 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-20 lg:py-28">
          <p className="overline mb-4">Advisory Services</p>
          <motion.h1 {...fade} className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
            Launch, grow, and scale<br /><span className="text-primary">world-class innovation ecosystems.</span>
          </motion.h1>
          <motion.p {...fade} transition={{ delay: 0.1 }} className="mt-8 text-lg text-muted-foreground max-w-2xl">
            We help organizations build the programs, teams, and communities that turn ambition into a thriving
            innovation ecosystem — from first concept to lasting impact.
          </motion.p>
          <motion.div {...fade} transition={{ delay: 0.2 }} className="mt-10">
            <a href={CONTACT} data-testid="advisory-cta-top" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-primary text-white hover:bg-[#09090B] transition-colors">
              Book a Strategy Call <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <p className="overline mb-3">What we do</p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl max-w-2xl">Advisory built for the innovation economy.</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
            {SERVICES.map((s, i) => (
              <motion.div
                {...fade}
                transition={{ delay: (i % 4) * 0.05 }}
                key={s.title}
                data-testid={`advisory-service-${i}`}
                className="bg-white p-7 flex flex-col"
              >
                <div className="h-11 w-11 bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <s.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading font-bold text-lg leading-tight">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.body}</p>
                <p className="overline text-[0.55rem] mt-6 mb-3">{s.kind}</p>
                <ul className="space-y-2">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm">
                      <Check className="h-3.5 w-3.5 text-primary mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <p className="overline mb-3">Our approach</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl">How We Work</h2>
            <p className="text-muted-foreground mt-4">A clear, collaborative path from understanding your goals to executing and sustaining them.</p>
          </div>
          <div className="mt-14 max-w-3xl mx-auto">
            {PROCESS.map((p, i) => (
              <motion.div {...fade} transition={{ delay: i * 0.05 }} key={p.title}>
                <div className="flex items-start gap-5" data-testid={`advisory-step-${i}`}>
                  <div className="relative flex flex-col items-center shrink-0">
                    <div className="h-12 w-12 bg-[#09090B] text-white flex items-center justify-center font-heading font-black">
                      {i + 1}
                    </div>
                  </div>
                  <div className="flex-1 border border-black/10 p-5 flex items-center gap-4">
                    <p.icon className="h-5 w-5 text-primary shrink-0" strokeWidth={1.75} />
                    <div>
                      <h3 className="font-heading font-bold">{p.title}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{p.body}</p>
                    </div>
                  </div>
                </div>
                {i < PROCESS.length - 1 && (
                  <div className="flex justify-start pl-[22px] py-2 text-muted-foreground/40">
                    <ArrowDown className="h-5 w-5" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section id="advisory-contact" className="border-t border-black/10 bg-[#F4F4F5] scroll-mt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline mb-3">Get in touch</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight">Tell us about your program.</h2>
            <p className="text-muted-foreground mt-4">Share a few details and we'll set up a strategy call to explore how we can help.</p>
          </div>
          <div className="lg:col-span-8">
            {done ? (
              <div className="bg-white border border-black/10 p-10 text-center" data-testid="advisory-form-success">
                <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-4" strokeWidth={1.75} />
                <h3 className="font-heading font-black text-2xl">Message received.</h3>
                <p className="text-muted-foreground mt-3 max-w-md mx-auto">Thanks for reaching out. We'll review your note and get back to you shortly to schedule your strategy call.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-black/10 p-8 space-y-5" data-testid="advisory-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Full name</Label><Input value={form.full_name} onChange={set("full_name")} required data-testid="advisory-name" className="bg-[#F4F4F5] border-black/10 rounded-none h-11" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="advisory-email" className="bg-[#F4F4F5] border-black/10 rounded-none h-11" /></div>
                  <div className="space-y-2"><Label>Organization</Label><Input value={form.organization} onChange={set("organization")} required data-testid="advisory-org" className="bg-[#F4F4F5] border-black/10 rounded-none h-11" /></div>
                  <div className="space-y-2"><Label>Role</Label><Input value={form.role} onChange={set("role")} placeholder="Your title" data-testid="advisory-role" className="bg-[#F4F4F5] border-black/10 rounded-none h-11" /></div>
                </div>

                <div className="space-y-2">
                  <Label>Program stage</Label>
                  <div className="flex flex-wrap gap-2">
                    {STAGES.map((s) => {
                      const on = stages.includes(s);
                      return (
                        <button type="button" key={s} onClick={() => toggle(stages, setStages, s)} data-testid={`advisory-stage-${s}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border transition-colors ${on ? "bg-[#09090B] text-white border-[#09090B]" : "border-black/15 text-muted-foreground hover:text-[#09090B] hover:border-black/30"}`}>
                          {on && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />} {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Services needed</Label>
                  <div className="flex flex-wrap gap-2">
                    {SERVICE_OPTIONS.map((s) => {
                      const on = services.includes(s);
                      return (
                        <button type="button" key={s} onClick={() => toggle(services, setServices, s)} data-testid={`advisory-service-opt-${s}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border transition-colors ${on ? "bg-primary text-white border-primary" : "border-black/15 text-muted-foreground hover:text-[#09090B] hover:border-black/30"}`}>
                          {on && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />} {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2"><Label>Message</Label><Textarea value={form.message} onChange={set("message")} data-testid="advisory-message" placeholder="What are you looking to build or improve?" className="bg-[#F4F4F5] border-black/10 rounded-none min-h-[120px]" /></div>

                <Button type="submit" disabled={submitting} data-testid="advisory-submit" className="rounded-none bg-primary hover:bg-[#09090B] transition-colors px-8 h-11">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send inquiry <ArrowRight className="h-4 w-4 ml-1" /></>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 bg-primary text-white overflow-hidden relative">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center relative">
          <h2 className="font-heading font-black text-3xl sm:text-4xl">Let's build something that lasts.</h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            Tell us where you want to take your programs, team, or ecosystem — and we'll help you get there.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a href={CONTACT} data-testid="advisory-cta-bottom" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-white text-primary hover:bg-[#09090B] hover:text-white transition-colors">
              Book a Strategy Call <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/for-clients" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border border-white/25 hover:bg-white/10 transition-colors">
              Explore recruiting
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
